import { useCallback, useSyncExternalStore } from "react"

export type TemperatureUnit = "celsius" | "fahrenheit" | "both"

const STORAGE_KEY = "temperature-unit"
const DEFAULT_UNIT: TemperatureUnit = "celsius"

function getSnapshot(): TemperatureUnit {
  return (localStorage.getItem(STORAGE_KEY) as TemperatureUnit) || DEFAULT_UNIT
}

function subscribe(callback: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) callback()
  }
  window.addEventListener("storage", handler)
  return () => window.removeEventListener("storage", handler)
}

/**
 * Hook to manage temperature unit preference (Celsius, Fahrenheit, or Both).
 * Persisted in localStorage.
 */
export function useTemperatureUnit() {
  const unit = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_UNIT)

  const setUnit = useCallback((newUnit: TemperatureUnit) => {
    localStorage.setItem(STORAGE_KEY, newUnit)
    // Force re-render across all components using this hook
    window.dispatchEvent(
      new StorageEvent("storage", { key: STORAGE_KEY, newValue: newUnit }),
    )
  }, [])

  return { unit, setUnit }
}

/**
 * Convert Celsius to Fahrenheit.
 */
export function celsiusToFahrenheit(celsius: number): number {
  return (celsius * 9) / 5 + 32
}

/**
 * Format a temperature value according to the user's unit preference.
 * Input value is always in Celsius (as stored in the database).
 */
export function formatTemperature(
  valueCelsius: number,
  unit: TemperatureUnit,
): string {
  const c = valueCelsius.toFixed(2)
  const f = celsiusToFahrenheit(valueCelsius).toFixed(2)

  switch (unit) {
    case "celsius":
      return `${c} °C`
    case "fahrenheit":
      return `${f} °F`
    case "both":
      return `${c} °C / ${f} °F`
  }
}
